import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAgentAlbertAlerts, AGENT_ALERTS_FETCH_LIMIT } from "../utils/api";
import { countUnreadAgentAlbertAlerts } from "../utils/agentAlbertAlertReadState";

const POLL_INTERVAL_MS = 60000;

/**
 * Resolves the "Shift Report" unread badge count for the current location,
 * polling the same endpoint the Dashboard uses and staying in sync with it
 * via the "agentAlbertAlertSeenUpdated" event + localStorage writes (see
 * utils/agentAlbertAlertReadState.js). Used by Sidebar to show a live count
 * next to "Shift Report" that decrements as alerts are opened/read.
 */
export function useShiftReportUnreadCount(activeLocation) {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const alertsRef = useRef([]);
  const locationRef = useRef("");

  const locationId = useMemo(
    () => String(activeLocation?._id || activeLocation?.id || "").trim(),
    [activeLocation],
  );

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    locationRef.current = locationId;
  }, [locationId]);

  useEffect(() => {
    setUnreadCount(countUnreadAgentAlbertAlerts(alerts, locationId));
  }, [alerts, locationId]);

  useEffect(() => {
    const onSeenUpdated = (ev) => {
      const lid = String(ev.detail?.locationId || "");
      if (lid !== locationRef.current) return;
      setUnreadCount(countUnreadAgentAlbertAlerts(alertsRef.current, lid));
    };
    window.addEventListener("agentAlbertAlertSeenUpdated", onSeenUpdated);
    return () => window.removeEventListener("agentAlbertAlertSeenUpdated", onSeenUpdated);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      const prefix = "agentAlbertAlertSeen:";
      if (!e.key || !e.key.startsWith(prefix)) return;
      const lid = e.key.slice(prefix.length);
      if (lid !== locationRef.current) return;
      setUnreadCount(countUnreadAgentAlbertAlerts(alertsRef.current, lid));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!locationId) {
      setAlerts([]);
      return undefined;
    }
    let active = true;
    const poll = async () => {
      try {
        const payload = await fetchAgentAlbertAlerts(locationId, AGENT_ALERTS_FETCH_LIMIT);
        if (!active) return;
        setAlerts(Array.isArray(payload?.alerts) ? payload.alerts : []);
      } catch {
        if (active) setAlerts([]);
      }
    };
    void poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [locationId]);

  return unreadCount;
}

export default useShiftReportUnreadCount;
